/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react';
import { JobStates } from '../../../../../common/src/domain/job/JobStates';
import { JobTypes } from '../../../../../common/src/domain/job/JobTypes';
import { GeoConversionTypes } from '../../../../../common/src/enums/GeoConversionTypes';
import { useTranslate } from '../../../mixins/translate/translate';
import { openApiFetchGet, openApiFetchPost } from '../../../store/openApiFetch';
import { EditPanelCell, EditPanelInput, EditPanelRow, EditPanelSection, EditPanelSelect, PanelBody } from '../../elements';
import { Spinner } from '../../spinners';
import { DisplayMessage } from '../../utils/displayMessage';
import { AssetDropzone } from '../Common/AssetDropzone';

async function loadStatus({ surveyId }: { surveyId: string }) {
    const result = await openApiFetchGet('/api/surveys/{surveyId}/jobs/{jobType}/status', { surveyId, jobType: JobTypes.PotreeConvert });
    return result;
}

function DisplayFailedJobMessage({ jobStatusMessage }: { jobStatusMessage: string | undefined }) {
    const translate = useTranslate();

    let message;
    if (jobStatusMessage) {
        if (jobStatusMessage.startsWith('Exit code is null')) {
            message = translate('worker.potreeFailedWithExitCodeNull');
        } else {
            message = `translate('worker.lastJobFailed') : ${jobStatusMessage}`;
        }
    }

    return <DisplayMessage message={message} messageType="error" />;
}

export function PotreeConvert(props: { objectId: string; surveyId: string; onConverted: () => void }) {
    const translate = useTranslate();
    const [geoConversion, setGeoConversion] = React.useState<GeoConversionTypes>(GeoConversionTypes.None);
    const [utmZone, setUtmZone] = React.useState<string | undefined>('0');
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);
    const [jobStatus, setJobStatus] = React.useState<{ message?: string; status: JobStates } | undefined>();
    const [uploadedAsset, setUploadedAsset] = React.useState<
        | {
              assetId: string;
              filename: string;
              filesize: number;
              geoConversionType: GeoConversionTypes;
              utmZone: string | undefined;
          }
        | undefined
    >();

    async function finishUpload() {
        if (uploadedAsset) {
            await openApiFetchPost(
                '/api/surveys/{surveyId}/jobs/{jobType}/finishUpload',
                { surveyId: props.surveyId, jobType: JobTypes.PotreeConvert },
                {
                    assetId: uploadedAsset.assetId,
                    filename: uploadedAsset.filename,
                    filesize: uploadedAsset.filesize,
                    objectId: props.objectId,
                    options: { geoConversionType: uploadedAsset.geoConversionType, utmZone: uploadedAsset.utmZone },
                },
            );
            setUploadedAsset(undefined);
            await pollStatus();
        }
    }

    async function pollStatus() {
        const status = await loadStatus({ surveyId: props.surveyId });
        setJobStatus(status);
        if (status.status === JobStates.NoJob || status.status === JobStates.Failed) {
            return;
        } else if (status.status === JobStates.Finished) {
            props.onConverted();
        } else {
            setTimeout(pollStatus, 5000);
        }
    }

    React.useEffect(() => {
        if (uploadedAsset) {
            void finishUpload();
        }
    }, [uploadedAsset]);

    React.useEffect(() => {
        async function init() {
            const status = await loadStatus({ surveyId: props.surveyId });
            setJobStatus(status);
            if (status.status === JobStates.Processing) {
                await pollStatus();
            }
        }
        void init();
    }, []);

    return (
        <PanelBody>
            <EditPanelRow>
                {jobStatus?.status === JobStates.Processing && <Spinner />}
                {jobStatus?.status === JobStates.Failed && <DisplayFailedJobMessage jobStatusMessage={jobStatus.message} />}

                {jobStatus && [JobStates.NoJob, JobStates.Finished, JobStates.Failed].includes(jobStatus?.status) && uploadedAsset === undefined && (
                    <>
                        {errorMessage && (
                            <DisplayMessage
                                message={errorMessage === 'Dropzone: FILE_TOO_BIG' ? translate('asset.errorFileTooBig') : errorMessage}
                                messageType="error"
                            />
                        )}
                        <EditPanelRow>
                            <EditPanelCell rowCount={3}>
                                <EditPanelSelect
                                    name="geoConversion"
                                    topLabel={translate('PotreeConverter.geoConversion')}
                                    value={geoConversion}
                                    handleChange={(value: any) => setGeoConversion(value)}
                                    options={[
                                        { text: translate(`PotreeConverter.${GeoConversionTypes.None}`), value: GeoConversionTypes.None },
                                        { text: translate(`PotreeConverter.${GeoConversionTypes.Wgs84}`), value: GeoConversionTypes.Wgs84 },
                                        { text: translate(`PotreeConverter.${GeoConversionTypes.Rds}`), value: GeoConversionTypes.Rds },
                                    ]}
                                />
                            </EditPanelCell>
                            <EditPanelCell rowCount={3}>
                                {geoConversion === GeoConversionTypes.Wgs84 && (
                                    <>
                                        <EditPanelInput
                                            value={utmZone}
                                            handleChange={(value) => setUtmZone(value.replace(/[^NS\d]/g, ''))}
                                            name="utmZone"
                                            topLabel={translate('PotreeConverter.utmZone')}
                                        />
                                    </>
                                )}
                            </EditPanelCell>
                        </EditPanelRow>
                        <EditPanelSection>
                            <AssetDropzone
                                acceptedFiles=".las,.laz"
                                onComplete={(file) => {
                                    if (file.status === 'success') {
                                        setUploadedAsset({
                                            assetId: file.s3Id,
                                            filename: file.name,
                                            filesize: file.upload!.total,
                                            geoConversionType: geoConversion,
                                            utmZone: geoConversion === GeoConversionTypes.Wgs84 ? utmZone : undefined,
                                        });
                                    }
                                }}
                                maxFileSizePerType={[
                                    { extension: 'las', sizeMb: 4500 },
                                    { extension: 'laz', sizeMb: 1500 },
                                ]}
                                onError={(e) => setErrorMessage(e)}
                                objectId={props.objectId}
                                surveyId={props.surveyId}
                                assetType="3DModel"
                                autoProcessQueue={true}
                            />
                        </EditPanelSection>
                        <div className="fs14 fw200">{translate('PotreeConverter.maxFileSize')}</div>
                    </>
                )}
            </EditPanelRow>
        </PanelBody>
    );
}
